import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import NewOffersSwiper from "../components/NewOffersSwiper";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import styles from "./MySearch.module.css";
import editIcon from "../images/eye.svg";
import deleteIcon from "../images/trash-xmark.svg";
import chevronLeft from "../images/chevron-left-outlined-rounded.svg";
import chevronRight from "../images/chevron-right-outlined-rounded.svg";

const MySearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("forSale");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [searchToDeleteId, setSearchToDeleteId] = useState(null);
  const [demands, setDemands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterZipcode, setFilterZipcode] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterSqm, setFilterSqm] = useState("");
  const [filterBedrooms, setFilterBedrooms] = useState("");
  const [filterPrice, setFilterPrice] = useState("");
  const [sortColumn, setSortColumn] = useState("");

  useEffect(() => {
    const fetchDemands = async () => {
      try {
        const response = await authFetch(
          `${BASE_URL}/property/latest/demands?limit=30`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch demands");
        }

        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await response.json();
          setDemands(data);
        } else {
          const text = await response.text();
          throw new Error(
            `Server returned a non-JSON response. Check your API route. Response was: "${text.substring(
              0,
              50
            )}..."`
          );
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching demands:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDemands();

    // const intervalId = setInterval(fetchDemands, 60000);

    return () => {
      // Polling disabled to prevent property changes during animation
      // clearInterval(intervalId);
    };
  }, []);


  const fetchUserSearches = async (page = 1, limit = itemsPerPage, offeringType = null) => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters with filters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (offeringType) params.append('offeringType', offeringType);
      if (searchQuery) params.append('searchQuery', searchQuery);
      if (filterZipcode) params.append('zipcode', filterZipcode);
      if (filterCity) params.append('city', filterCity);
      if (filterType) params.append('propertyType', filterType);
      if (filterSqm) params.append('minSqm', filterSqm);
      if (filterBedrooms) params.append('bedrooms', filterBedrooms);
      if (filterPrice) params.append('minPrice', filterPrice);

      const response = await authFetch(
        `${BASE_URL}/property/user-properties?${params.toString()}`
      );

      if (!response.ok) {
        let errorBody;
        try {
          errorBody = await response.json();
        } catch (e) {
          errorBody = await response.text();
        }
        const errorMessage =
          errorBody.message ||
          JSON.stringify(errorBody) ||
          response.statusText ||
          "Unknown error";
        console.error(`API Error ${response.status}:`, errorMessage);
        throw new Error(`API Error: ${response.status} - ${errorMessage}`);
      }

      const result = await response.json();

      if (result && result.data) {
        const formattedSearches = result.data.map((demand) => ({
          id: demand.id,
          displayId: demand?.displayId || "-",
          offeringType: demand.offeringType || "-",
          propertyType: demand.propertyType
            ? demand.propertyType.toUpperCase()
            : "-",
          price: `${demand.price} ${demand.currency}` || "-",
          bedrooms: demand.bedrooms || "-",
          units: demand.units || "-",
          squareMeters: demand.squareMeters || "-",
          city: demand.PropertyAddresses?.[0]?.city || "-",
          zip: demand.PropertyAddresses?.[0]?.zipCode || "-",
          mustCriteria: demand.DemandPropertyCriterium?.mustCriteria || {},
          shouldCriteria:
            demand.DemandPropertyCriterium?.shouldCriteria || {},
        }));
        setSearches(formattedSearches);
        setTotalCount(result.total || 0);
      } else {
        console.error(
          "API response structure unexpected after JSON parsing:",
          result
        );
        setError(
          "API response did not contain expected data (data array missing or not array)."
        );
      }
    } catch (err) {
      console.error("Error fetching user searches in MySearch:", err);
      setError(
        err.message ||
          "Failed to load your searches. Please check your network or authentication."
      );
    } finally {
      setLoading(false);
    }
  };

  // Debounced effect for filter changes
  useEffect(() => {
    const offeringType = activeTab === "forSale" ? "purchase" : "rental";
    const isFilterChange = searchQuery || filterZipcode || filterCity || filterType || filterSqm || filterBedrooms || filterPrice;

    if (!isFilterChange) {
      // No filters applied, fetch immediately
      fetchUserSearches(currentPage, itemsPerPage, offeringType);
      return;
    }

    // Debounce filter changes
    const debounceTimer = setTimeout(() => {
      fetchUserSearches(currentPage, itemsPerPage, offeringType);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [activeTab, currentPage, itemsPerPage, searchQuery, filterZipcode, filterSqm, filterBedrooms, filterCity, filterType, filterPrice]);

  // Reset to first page when filters change (but not on initial mount)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchQuery, filterZipcode, filterSqm, filterBedrooms, filterCity, filterType, filterPrice]);

  // Backend handles filtering and pagination - just display the results
  const currentItems = searches;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const handleDelete = (id) => {
    setSearchToDeleteId(id);
    setShowConfirmModal(true);
  };
  const confirmDeleteSearch = async () => {
    setShowConfirmModal(false);
    if (!searchToDeleteId) return;

    try {
      toast.info(t("attempting_delete_search_toast"));
      const response = await authFetch(
        `${BASE_URL}/property/${searchToDeleteId}`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        let errorBody = await response.json();
        throw new Error(errorBody.message || t("failed_delete_search_error"));
      }

      setSearches((prev) =>
        prev.filter((search) => search.id !== searchToDeleteId)
      );
      toast.success(t("search_deleted_successfully_toast"));
    } catch (error) {
      console.error("Error deleting Search:", error);
      toast.error(`${t("error_deleting_search_toast")}: ${error.message}`);
    } finally {
      setSearchToDeleteId(null);
    }
  };
  const cancelDeleteSearch = () => {
    setShowConfirmModal(false);
    setSearchToDeleteId(null);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setFilterZipcode("");
    setFilterCity("");
    setFilterType("");
    setFilterSqm("");
    setFilterBedrooms("");
    setFilterPrice("");
    setSortColumn("");
  };

  const handleAddSearchClick = () => {
    // Navigate to SearchProperty page to create a new search
    // Pass the activeTab as a URL parameter to auto-select the offering type
    const offeringType = activeTab === "forSale" ? "purchase" : "rental";
    navigate(`/SearchProperty?offeringType=${offeringType}`);
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftPanel}>
        {!isLoading && !error && demands.length > 0 && (
          <div className={styles.newSearchesHeader}>
            <h2 className={styles.newSearchesTitle}>{t("new_demands")}</h2>
          </div>
        )}
        <div className={styles.propertyList}>
          {demands.length > 0 && (
            <NewOffersSwiper items={demands} className={styles.mySwiper} />
          )}
        </div>
      </div>

      <div className={styles.rightPanel}>
        <h2 className={styles.pageHeader}>{t("listed")}</h2>

        <div className={styles.headerContainer}>
          <div className={styles.tabsGroup}>
            <div className={styles.tabsContainer}>
              <button
                className={`${styles.tabButton} ${
                  activeTab === "forSale" ? styles.activeTab : styles.inactiveTab
                }`}
                onClick={() => {
                  setActiveTab("forSale");
                  setCurrentPage(1);
                }}
              >
                {t("for-sale")}
              </button>
              <button
                className={`${styles.tabButton} ${
                  activeTab === "forRental" ? styles.activeTab : styles.inactiveTab
                }`}
                onClick={() => {
                  setActiveTab("forRental");
                  setCurrentPage(1);
                }}
              >
                {t("for-rent")}
              </button>
            </div>
          </div>
          <div className={styles.searchAndAddGroup}>
            <input
              type="text"
              placeholder={t("search_all_columns") || "Search all columns..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.headerSearchInput}
            />
            <button
              className={styles.addLocationButton}
              onClick={handleAddSearchClick}
            >
              +
            </button>
          </div>
        </div>

        {/* Filter and Sort Bar */}
        <div className={styles.filterBar}>
          {/* <input
            type="text"
            placeholder={t("search_by_property_title_type") || "Search by property title, type"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchFilterInput}
          /> */}

     

          <input
            type="text"
            placeholder={t("filter_by_zipcode") || "Filter by zip code..."}
            value={filterZipcode}
            onChange={(e) => setFilterZipcode(e.target.value)}
            className={styles.filterTextInput}
          />

          <input
            type="text"
            placeholder={t("filter_by_city") || "Filter by city..."}
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className={styles.filterTextInput}
          />

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.filterDropdown}
          >
            <option value="">{t("property_any") || "Property Type: Any"}</option>
            <option value="house/villa">{t("house/villa") || "House/Villa"}</option>
            <option value="apartment">{t("apartment") || "Apartment"}</option>
            <option value="multifamily">{t("multifamily") || "Multifamily"}</option>
            <option value="plot">{t("plot") || "Plot"}</option>
          </select>

          <input
            type="number"
            placeholder={t("filter_by_sqm") || "Filter by sqm..."}
            value={filterSqm}
            onChange={(e) => setFilterSqm(e.target.value)}
            className={styles.filterTextInput}
          />

          <input
            type="number"
            placeholder={t("Bedrooms_Units_Index") || "Bedrooms/Units/Index"}
            value={filterBedrooms}
            onChange={(e) => setFilterBedrooms(e.target.value)}
            className={styles.filterTextInput}
          />

          <input
            type="number"
            placeholder={t("filter_by_price") || "Filter by price..."}
            value={filterPrice}
            onChange={(e) => setFilterPrice(e.target.value)}
            className={styles.filterTextInput}
          />

          <select
            value={sortColumn}
            onChange={(e) => setSortColumn(e.target.value)}
            className={styles.filterDropdown}
          >
            <option value="">{t("sort_by") || "Sort by"}</option>
            <option value="zipcode">{t("zip-code") || "Zip Code"}</option>
            <option value="sqm">{t("Sqm") || "sqm"}</option>
            <option value="bedrooms">{t("Bedrooms") || "Bedrooms"}</option>
            <option value="city">{t("table_header_city") || "City"}</option>
            <option value="type">{t("table_header_type") || "Type"}</option>
            <option value="price">{t("table_header_price") || "Price"}</option>
          </select>

          {/* <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className={styles.filterDropdown}
            disabled={!sortColumn}
          >
            <option value="asc">{t("ascending") || "Ascending"}</option>
            <option value="desc">{t("descending") || "Descending"}</option>
          </select> */}

          <button
            className={styles.clearButton}
            onClick={handleClearFilters}
          >
            {t("clear_filters") || "Clear Filters"}
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '15px', marginTop: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label htmlFor="rowsPerPage">{t("rows_per_page") || "Rows per page"}:</label>
            <select
              id="rowsPerPage"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.filterDropdown}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div className={styles.tableContainer}>
          <table className={styles.searchTable}>
            <thead>
              <tr>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("property_id")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("zip")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("city")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("type")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Sqm")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Bedrooms")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Units")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("Index")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}>{t("priceoffer")}</th>
                <th className={styles.tableHeader} style={{ textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px' }}>
                    {t("loading_properties") || "Loading properties..."}
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                    {t("error_loading_searches", { error: error })}
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((search, index) => (
                  <tr
                    key={search.id}
                    className={styles.tableRow}
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? "rgba(41, 177, 133, 0.25)"
                          : "rgba(204, 204, 204, 0.4)",
                    }}
                  >
                    <Link
                      to={`/SearchView/${search.id}`}
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "contents",
                      }}
                    >
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit" }}
                      >
                        {search?.displayId}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.zip}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.city}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.propertyType}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.squareMeters}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.propertyType !== 'multifamily' && search.propertyType !== 'plot'
                          ? (search.bedrooms || '-')
                          : '-'}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.propertyType === 'multifamily'
                          ? (search.units || '-')
                          : '-'}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.propertyType === 'plot'
                          ? (search.index || '-')
                          : '-'}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "inherit", textAlign: 'center' }}
                      >
                        {search.price}
                      </td>
                      <td
                        className={styles.tableCell}
                        style={{ backgroundColor: "white" }}
                      >
                        <div className={styles.actionButtons}>
                          <Link
                            to={`/SearchView/${search.id}`}
                            className={styles.iconButton}
                            title={t("view_or_edit")}
                          >
                            <img
                              src={editIcon}
                              alt={t("edit")}
                              className={styles.iconImage}
                            />
                          </Link>
                          <button
                            className={styles.iconButton}
                            onClick={(e) => {
                              e.preventDefault();
                              handleDelete(search.id);
                            }} // Prevent Link navigation
                            title={t("delete")}
                          >
                            <img
                              src={deleteIcon}
                              alt={t("delete")}
                              className={styles.iconImage}
                            />
                          </button>
                        </div>
                      </td>
                    </Link>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#777",
                    }}
                  >
                    {t("no_searches_found", {
                      type: activeTab === "forSale" ? t("sale") : t("rental"),
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {/* Pagination Controls */}
          {totalPages > 1 && ( // Only show pagination if there's more than one page - match MyProperty
            <div className={styles.paginationContainer} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '10px 0', marginTop: '0' }}>
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={styles.paginationButton}
              >
                <img src={chevronLeft} alt="Previous" style={{ width: '20px', height: '20px' }} />
              </button>
              {(() => {
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                let end = start + maxVisible - 1;
                if (end > totalPages) {
                  end = totalPages;
                  start = Math.max(1, end - maxVisible + 1);
                }
                return Array.from({ length: end - start + 1 }, (_, i) => start + i).map((number) => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`${styles.paginationButton} ${currentPage === number ? styles.activePaginationButton : ""}`}
                  >
                    {number}
                  </button>
                ));
              })()}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
              >
                <img src={chevronRight} alt="Next" style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
          )}
        </div>
      </div>
      {showConfirmModal && (
        <div className={styles.modalOverlay}>
          {" "}
          {/* Assuming modal styles are in MySearch.module.css */}
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>
              {t("confirm_delete_search_title")}
            </h3>{" "}
            {/* New translation key */}
            <p className={styles.modalMessage}>
              {t("confirm_delete_search_message")}
            </p>{" "}
            {/* New translation key */}
            <div className={styles.modalButtons}>
              <button
                className={`${styles.modalButton} ${styles.modalButtonConfirm}`}
                onClick={confirmDeleteSearch} // Calls the new confirm function
              >
                {t("yes_delete_search_button")} {/* New translation key */}
              </button>
              <button
                className={`${styles.modalButton} ${styles.modalButtonCancel}`}
                onClick={cancelDeleteSearch} // Calls the new cancel function
              >
                {t("cancel_button")}{" "}
                {/* Reuse this key if it fits, or create a new one like 'cancel_search_button' */}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySearch;
